/**
 * The pure `PfStoreState → PlaceOrderV2DTO` serializer: the body `POST /workspace/{ws}/orders`
 * accepts, built from the cart and the resolved catalogue.
 *
 * The cart keys on Salesforce ids; the wire on `productCode`, resolved here against the catalogue.
 * One line per cart item (`serializeLine`), with its configuration (`serializeLineConfiguration`);
 * the meeting becomes the organization method (`serializeOrganizationMethod`). Kept pure so the
 * place-order hook and tests can call it directly; the hook owns the logging.
 */

import type {
  AddressDTO,
  BillingMethodDTO,
  OrderPaymentMethodCode,
  OrderProductDTO,
  PaymentMethod,
  PlaceOrderAdminV2DTO,
  PlaceOrderClientV2DTO,
  PlaceOrderV2DTO,
  ResolvedCatalogue
} from 'models/purchaseFlowRevamp'
import { defaultTimezone } from 'utils/time/timezoneUtils'
import { ProductKind } from 'constants/product/productTypes'
import type { OrderLaunchedFrom } from 'models/purchaseFlow/orderLaunchedFrom'

import { selectInstructionsStepSkipped } from './selectors'
import { buildProduct } from './serializeLine'
import { orderLevelConfigs } from './serializeLineConfiguration'
import { buildOrganizationMethod } from './serializeOrganizationMethod'
import { shootOutputLines } from './shootOutput'
import type { PfStoreState } from './store'
import { compact } from './wire'

/**
 * FE payment method → the wire `paymentMethod`. Card and SEPA go through Stripe, invoice is by e-mail.
 *
 * `card` echoes the org's own card kind (`useBillingPaymentMethods().cardPaymentKind`):
 * `CARD_PAYMENT` and `STRIPE_PAYMENT` are distinct billing-catalogue kinds and the backend rejects
 * one the org is not catalogued for. `STRIPE_PAYMENT` is the fallback while that is still unknown.
 */
export function mapPaymentMethod(pm: PaymentMethod, cardPaymentKind?: ProductKind): OrderPaymentMethodCode {
  switch (pm) {
    case 'card':
      return cardPaymentKind === ProductKind.CARD_PAYMENT ? 'CARD_PAYMENT' : 'STRIPE_PAYMENT'
    case 'sepa':
      return 'SEPA_DIRECT_DEBIT'
    case 'invoice':
    default:
      return 'INVOICE_BY_EMAIL'
  }
}

/* -------------------------------------------------------------------------- *
 *  Serializer
 * -------------------------------------------------------------------------- */

/** Options for {@link serializePlacement}. */
export interface SerializePlacementOptions {
  /**
   * Resolved user timezone (from `getUserTimezone` / `useTimezone().userTimezone`).
   * Wire-required org-method `timezone` fallback for the modes the FE forms don't
   * collect a tz for. Defaults to the platform `defaultTimezone` (Europe/Zurich)
   * so the pure fn stays callable without a caller — but real callers MUST pass
   * the resolved value.
   */
  userTimezone?: string
  /**
   * The backend's site-access verdict for this cart
   * (`useInstructionOptions().data?.visible`).
   *
   * It must be the SAME value the Instructions step was gated on, or the order
   * disagrees with the screen: a step shown by the flag but skipped by the
   * cart-shape fallback would drop the meeting the client just entered.
   * `undefined` falls through to that fallback, which is what the step does too.
   */
  instructionsVisible?: boolean
  /**
   * Nothing on this order is charged (`useIsFreeCart`). The billing method is then omitted even when
   * one was chosen earlier: a card or SEPA method on a free order has no payment intent behind it.
   */
  nothingToCharge?: boolean
  /**
   * The SHOOT-location timezone from the instruction catalogue
   * (`useInstructionCatalogue().timezone`).
   *
   * This is the one the wire wants: the client enters the meeting time in the
   * shoot's zone (the picker is bound to it, and DF-I3's banner says so), so
   * sending the CLIENT's zone relabels that instant as a different moment for
   * anyone reading the order. Legacy sent the catalogue timezone
   * (`PurchaseFlowPlaceOrder.context.tsx`); v2 sent the user's for ON_SITE and
   * KEY_PICKUP, which only became reachable-and-wrong once timezones started
   * resolving at all.
   *
   * Falls back to `userTimezone` when the BE resolved none — better than
   * omitting a wire-required field, and identical to today's behaviour in that
   * case.
   */
  locationTimezone?: string
  /**
   * Actual wire kind (`CARD_PAYMENT` | `STRIPE_PAYMENT`) behind the FE `'card'`
   * bucket for THIS session, resolved by the caller from the BILLING
   * instruction-options response (`useBillingPaymentMethods().cardPaymentKind`).
   * Passed through to `mapPaymentMethod` so an org catalogued under
   * `CARD_PAYMENT` doesn't get a hardcoded `STRIPE_PAYMENT` rejected at place
   * order (R-3 CLOSED, ledger #14). `undefined` falls back to `STRIPE_PAYMENT`.
   */
  cardPaymentKind?: ProductKind
  /**
   * The BE-preferred method, used when the client never clicked a card.
   *
   * §11.1 forbids seeding `paymentMethod` on mount, and the only writer is a
   * card click, so a client who simply accepts the preselect leaves the slice
   * empty. The Billing and Summary gates already resolve
   * `paymentMethod ?? defaultPaymentMethod`; without the same fallback here the
   * gates open and the order cannot be built — a silent dead end.
   */
  defaultPaymentMethod?: PaymentMethod
  /**
   * Render-time billing defaults — the reference derived from
   * the property and the client's saved CC list. Same contract as
   * `defaultPaymentMethod`: shown without seeding the slice, so the wire has to
   * fall back to them or the order would omit what the client saw.
   */
  defaultReference?: string
  defaultCcEmails?: ReadonlyArray<string>
  /**
   * The derived single-saved-address default, threaded exactly as
   * `defaultPaymentMethod` is. Without it, opening the Billing gate on that
   * default would make this function return null and the place-order mutation
   * throw: a failed order instead of a dead button.
   */
  defaultInvoicingAddress?: AddressDTO
  /** The flow mount's attempt id (`usePfOrderAttemptId`); omitted when absent. */
  orderAttemptId?: string
  /** Where the flow was opened from (`usePfOrderLaunchedFrom`); omitted when absent. */
  launchedFrom?: OrderLaunchedFrom
}

/**
 * Build the v2 place-order wire body from store state + the resolved catalogue.
 *
 * Returns `null` when a hard precondition is missing (`sessionId` or a saved
 * `invoicingAddress`) — mirrors the `buildBody` guards. Note `paymentMethod` is
 * NOT one: since ISSUE-3107 an order with nothing chargeable legitimately
 * carries no billing method. Per-item lines
 * that can't be resolved against the catalogue make the whole body `null`: an
 * order shorter than the cart the client approved must not go out.
 *
 * The admin vs client body is chosen by admin scope (`targetUserId` /
 * `targetWorkspaceId`). `role` is never included — BE derives it from the JWT
 * (KB H7). Kept pure: the caller injects `userTimezone`.
 */
export function serializePlacement(
  state: PfStoreState,
  catalogue: ResolvedCatalogue | undefined,
  { userTimezone = defaultTimezone, locationTimezone, cardPaymentKind, defaultPaymentMethod, defaultReference, defaultCcEmails, defaultInvoicingAddress, instructionsVisible, nothingToCharge = false, orderAttemptId, launchedFrom }: SerializePlacementOptions = {}
): PlaceOrderV2DTO | null {
  const sessionId = state.draft.sessionId
  if (!sessionId) return null
  // The backend files the order under the session's property, whatever the screen shows.
  const { sessionPropertyId } = state.draft
  if (sessionPropertyId && sessionPropertyId !== state.property?.id) return null
  // The invoicing address is as hard a precondition as the session.
  // It used to be optional-chained onto the body, so a state without one built a
  // complete, sendable order with the field merely absent, and nothing
  // downstream throws. `selectBillingStepValidFor` demands an address with a
  // BE-issued id for EVERY payment method (free carts included), so no real
  // placement reaches here without one — this is what keeps that true if the UI
  // gate is relaxed, which is what ISSUE-2963 proposes. An empty id is the
  // legacy `'new'` sentinel: an address the client started and never saved.
  // The EFFECTIVE address — same `??` precedence the Billing gate judges by,
  // so what opened the gate is what gets sent.
  const invoicingAddress = state.invoicingAddress ?? defaultInvoicingAddress
  if (!invoicingAddress?.id) return null
  // The EFFECTIVE method — the same value the step gates judge. Absent is
  // now legal: since ISSUE-3107 the BE accepts an order with no `billingMethod`
  // when nothing on it is chargeable, which is how a 0 EUR cart is placed.
  const effectivePaymentMethod = state.paymentMethod ?? defaultPaymentMethod

  // Resolved once for the whole cart: an order-level answer belongs to every
  // line of its flow, not only to the line the client happened to fill in.
  const sharedConfigs = orderLevelConfigs(state.productConfigs)

  // A line made from the shoot's photos sends neither files nor a configuration, whatever an earlier
  // standalone edit left in the store: the Configure step no longer shows it.
  const fromShoot = shootOutputLines(state.items, catalogue?.shellProducts)
  const products = state.items
    .map((item) => buildProduct(item, catalogue, state.attachments, sharedConfigs, state.productConfigs, fromShoot.has(item.id)))
    .filter((p): p is OrderProductDTO => p !== null)
  // A line the catalogue cannot resolve would leave the order shorter than the cart the client approved.
  if (products.length !== state.items.length) return null

  // Omitted on a free order: a method chosen while the cart still cost money (a
  // OneSystem quota can cover every pack) would otherwise reach placement with no
  // payment intent behind it. The BE takes a free order with no method.
  const billingMethod: BillingMethodDTO | undefined = effectivePaymentMethod && !nothingToCharge
    ? { paymentMethod: mapPaymentMethod(effectivePaymentMethod, cardPaymentKind) }
    : undefined

  // A cart whose products need no instructions never reaches that step, so
  // anything still in its slice was entered for a DIFFERENT cart — the client
  // removed the shoot, or came back and swapped the products. Sending it is not
  // merely stale, it is rejected: the backend derives the site-access product
  // from `organizationMethod` within the ORDER's category, and an authorities
  // document has no such product, so placement 404s on
  // "OrganizationProduct MEETING_ON_SITE not found".
  //
  // The predicate is the step's own AND is given the same backend verdict the
  // step was gated on, so what we send and what we asked for cannot disagree.
  // Passing the flag is not optional: the backend judges per line and the
  // cart-shape fallback per category, so a cart the flag shows and the fallback
  // skips would silently drop the meeting the client had just entered.
  const instructionsSkipped = selectInstructionsStepSkipped(catalogue, instructionsVisible)(state)

  const organizationMethod = instructionsSkipped
    ? undefined
    : buildOrganizationMethod(state.meeting, userTimezone, locationTimezone)

  // Favourite-creative: FE keeps `ctBookingId` as a string id; the wire wants a
  // numeric id. Map only when it parses cleanly. Dropped with the meeting — a
  // preferred photographer means nothing on an order with no shoot.
  const favourite = !instructionsSkipped
    && state.ctBookingId != null && state.ctBookingId !== '' && Number.isFinite(Number(state.ctBookingId))
    ? Number(state.ctBookingId)
    : undefined

  // Effective CC list: the stored one once touched, else what the client was
  // shown. `ccEmailsTouched` is session state, so an emptied list that
  // was never re-saved falls back to the prefill — the same value on screen.
  const effectiveCcEmails = state.ccEmailsTouched || state.ccEmails.length > 0
    ? state.ccEmails
    : (defaultCcEmails ?? [])
  const ccEmails = effectiveCcEmails.length > 0 ? [...effectiveCcEmails] : undefined

  const base: PlaceOrderClientV2DTO = compact<PlaceOrderClientV2DTO>({
    sessionId,
    products,
    billingMethod,
    // Was hard-coded `false`, which meant a user could toggle
    // "Fast delivery" in Instructions, see `+39 EUR` in the cart total, and have
    // the placed order say `fastDelivery: false` — paid-for-nothing. The store
    // holds order-level extras as ProductKind STRINGS (§11.4), and
    // `PlaceOrderBaseV2DTO` has exactly two slots for them: this flag and
    // `selectedFavouriteCreativeId` (fed from `ctBookingId` below). Any OTHER
    // DELIVERY kind the BE offers still has no wire slot — R-4, open BE question.
    fastDelivery: state.extraServices.includes(ProductKind.FAST_DELIVERY),
    invoicingAddressId: invoicingAddress.id,
    organizationMethod,
    comments: state.comments,
    reference: state.reference ?? defaultReference,
    propertyOwner: state.propertyOwner,
    externalReporting: state.externalReporting,
    ccEmails,
    selectedFavouriteCreativeId: favourite,
    // Equal to the session's property whenever it recorded one (refused above otherwise).
    realEstatePropertyId: state.property?.id,
    orderAttemptId,
    launchedFrom,
  })

  const isAdminScoped = state.targetUserId !== null || state.targetWorkspaceId !== null
  if (!isAdminScoped) return base

  const admin: PlaceOrderAdminV2DTO = {
    ...base,
    // RESOLVED (Slice C): the admin gate captures the target client email into
    // `state.targetUserEmail`. '' stays as the loud fallback if it wasn't set
    // (would 422 server-side, surfacing the gap rather than hiding it).
    email: state.targetUserEmail ?? '',
    skipAutomation: false,
    skipCtAutomation: state.adminSkipCtAutomation,
    skipExternalProviderSending: state.adminSkipExternalProviderSending,
  }
  return admin
}
